import React from "react";
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { PATRONS } from "../data/events";

const COLS = [
  {
    heading: "The Portal",
    links: [
      { id: "footer-charity", label: "Charity Balls" },
      { id: "footer-awards", label: "Business Awards" },
      { id: "footer-highland", label: "Highland Balls" },
      { id: "footer-full-season", label: "The Full Season" },
    ],
  },
  {
    heading: "The House",
    links: [
      { id: "footer-about", label: "About BlackTieDo" },
      { id: "footer-editors", label: "The Editors" },
      { id: "footer-journal", label: "The Journal" },
      { id: "footer-contact", label: "Correspondence" },
    ],
  },
  {
    heading: "Fine Print",
    links: [
      { id: "footer-terms", label: "Terms" },
      { id: "footer-privacy", label: "Privacy" },
      { id: "footer-cookies", label: "Cookies" },
      { id: "footer-code", label: "Code of Conduct" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[color:var(--bt-bg)] border-t border-[color:var(--bt-gold-line)] pt-24 md:pt-32 px-6 md:px-10"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Links + contact */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-6 pb-16 md:pb-24">
          <div className="md:col-span-6">
            <div className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] mb-6">
              - Correspondence
            </div>
            <p className="font-serif-display text-3xl md:text-5xl leading-tight text-[color:var(--bt-ink)] max-w-xl">
              Should you require an evening we have not published, do write.
            </p>
            <a
              href="mailto:concierge@blacktiedo.co.uk"
              data-testid="footer-email"
              className="mt-8 inline-flex items-center gap-3 text-[color:var(--bt-gold)] hover:text-[color:var(--bt-gold-bright)] transition-colors"
            >
              <span className="font-serif-display italic text-2xl">
                concierge@blacktiedo.co.uk
              </span>
            </a>
          </div>

          {COLS.map((col) => (
            <div key={col.heading} className="md:col-span-2">
              <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-5">
                {col.heading}
              </div>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.id}>
                    <a
                      data-testid={link.id}
                      href="#"
                      className="text-sm text-[color:var(--bt-ink)]/80 hover:text-[color:var(--bt-gold)] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Societies & Patrons - text-only, quietly curated */}
        <motion.section
          data-testid="footer-societies"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="border-t border-[color:var(--bt-gold-line)] py-16 md:py-24"
          aria-label="Patrons & Societies"
        >
          <div className="grid md:grid-cols-12 gap-10 md:gap-12">
            <div className="md:col-span-4">
              <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] mb-4">
                - Patrons &amp; Societies
              </div>
              <p className="font-serif-display text-2xl md:text-3xl leading-snug text-[color:var(--bt-ink)] max-w-sm">
                Quietly curated on behalf of a number of long-standing London
                and regional heritage societies.
              </p>
            </div>

            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {PATRONS.map((group) => (
                <div
                  key={group.heading}
                  data-testid={`society-group-${group.heading
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-4 pb-3 border-b border-[color:var(--bt-gold-line)]">
                    {group.heading}
                  </div>
                  <ul className="space-y-2.5">
                    {group.societies.map((name) => (
                      <li
                        key={name}
                        data-testid={`society-${name
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                        className="font-serif-display italic text-[color:var(--bt-ink)]/85 text-base md:text-lg leading-tight"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Giant wordmark */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
          className="border-t border-[color:var(--bt-gold-line)] pt-14 pb-10"
          data-testid="footer-giant-wordmark"
        >
          <Wordmark size="xl" />
        </motion.div>

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[color:var(--bt-gold-line)] py-8 text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
          <div>© MMXXVI - BlackTieDo Portal, London</div>
          <div className="flex items-center gap-8">
            <span data-testid="footer-status">
              <span className="inline-block w-1.5 h-1.5 bg-[color:var(--bt-gold)] mr-2 align-middle" />
              Season MMXXVI · Open
            </span>
            <span>Est. 2026</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
